import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const reducedMotionQuery = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
);

export const prefersReducedMotion = () => reducedMotionQuery.matches;

export const initPreloader = () => {
  const preloader = document.querySelector("[data-preloader]");
  const value = document.querySelector("[data-loader-value]");

  if (!preloader || !value) {
    document.body.classList.add("is-page-ready");
    return;
  }

  document.body.classList.add("is-loading");

  let isComplete = false;
  const complete = () => {
    if (isComplete) {
      return;
    }

    isComplete = true;
    document.documentElement.style.setProperty("--load-progress", "1");
    value.textContent = "100";
    preloader.classList.add("is-complete");
    document.body.classList.remove("is-loading");
    document.body.classList.add("is-page-ready");
  };

  if (prefersReducedMotion()) {
    complete();
    return;
  }

  const startedAt = performance.now();
  const duration = 1150;
  const safetyTimer = window.setTimeout(complete, 2200);

  const update = (timestamp) => {
    const elapsed = timestamp - startedAt;
    const rawProgress = Math.min(elapsed / duration, 1);
    const easedProgress = 1 - Math.pow(1 - rawProgress, 3);
    const numericValue = Math.round(easedProgress * 100);

    document.documentElement.style.setProperty(
      "--load-progress",
      easedProgress.toFixed(4),
    );
    value.textContent = String(numericValue).padStart(3, "0");

    if (rawProgress < 1) {
      window.requestAnimationFrame(update);
      return;
    }

    window.setTimeout(() => {
      window.clearTimeout(safetyTimer);
      complete();
    }, 180);
  };

  window.requestAnimationFrame(update);
};

export const initRevealObserver = () => {
  const elements = [...document.querySelectorAll("[data-reveal]")];

  if (elements.length === 0) {
    return;
  }

  if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
    elements.forEach((element) => element.classList.add("is-visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.08,
    },
  );

  elements.forEach((element) => observer.observe(element));
};

export const initScrollProgress = () => {
  const root = document.documentElement;
  const header = document.querySelector("[data-header]");
  let ticking = false;

  const update = () => {
    const scrollable = Math.max(
      document.documentElement.scrollHeight - window.innerHeight,
      1,
    );
    const progress = Math.min(Math.max(window.scrollY / scrollable, 0), 1);

    root.style.setProperty("--scroll-progress", progress.toFixed(5));
    header?.classList.toggle("is-scrolled", window.scrollY > 24);
    ticking = false;
  };

  const requestUpdate = () => {
    if (ticking) {
      return;
    }

    ticking = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate, { passive: true });
  update();
};

export const initSectionTracking = () => {
  const sections = [...document.querySelectorAll("[data-section]")];
  const links = [...document.querySelectorAll("[data-nav-link]")];

  if (!("IntersectionObserver" in window) || sections.length === 0) {
    return;
  }

  const activate = (sectionId) => {
    links.forEach((link) => {
      const isActive = link.dataset.navLink === sectionId;
      link.classList.toggle("is-active", isActive);

      if (isActive) {
        link.setAttribute("aria-current", "page");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting)
        .sort(
          (first, second) => second.intersectionRatio - first.intersectionRatio,
        );

      if (visibleEntries.length > 0) {
        activate(visibleEntries[0].target.dataset.section);
      }
    },
    {
      rootMargin: "-30% 0px -55% 0px",
      threshold: [0.05, 0.2, 0.5],
    },
  );

  sections.forEach((section) => observer.observe(section));
};

export const initPointerField = () => {
  if (prefersReducedMotion() || window.matchMedia("(hover: none)").matches) {
    return;
  }

  const root = document.documentElement;
  const cursor = document.querySelector("[data-cursor]");
  const hoverTargets = document.querySelectorAll("a, button, [data-case-card]");
  const pointer = {
    targetX: window.innerWidth / 2,
    targetY: window.innerHeight / 2,
    glowX: window.innerWidth / 2,
    glowY: window.innerHeight / 2,
    cursorX: window.innerWidth / 2,
    cursorY: window.innerHeight / 2,
  };
  let frame = 0;

  const renderPointer = () => {
    pointer.glowX += (pointer.targetX - pointer.glowX) * 0.12;
    pointer.glowY += (pointer.targetY - pointer.glowY) * 0.12;
    pointer.cursorX += (pointer.targetX - pointer.cursorX) * 0.34;
    pointer.cursorY += (pointer.targetY - pointer.cursorY) * 0.34;

    root.style.setProperty("--pointer-x", `${pointer.glowX.toFixed(2)}px`);
    root.style.setProperty("--pointer-y", `${pointer.glowY.toFixed(2)}px`);
    root.style.setProperty("--cursor-x", `${pointer.cursorX.toFixed(2)}px`);
    root.style.setProperty("--cursor-y", `${pointer.cursorY.toFixed(2)}px`);

    const distance =
      Math.abs(pointer.targetX - pointer.glowX) +
      Math.abs(pointer.targetY - pointer.glowY) +
      Math.abs(pointer.targetX - pointer.cursorX) +
      Math.abs(pointer.targetY - pointer.cursorY);

    if (distance > 0.4) {
      frame = window.requestAnimationFrame(renderPointer);
      return;
    }

    frame = 0;
  };

  const requestPointerRender = () => {
    if (!frame) {
      frame = window.requestAnimationFrame(renderPointer);
    }
  };

  const updatePointer = (event) => {
    pointer.targetX = event.clientX;
    pointer.targetY = event.clientY;
    cursor?.classList.add("is-visible");
    requestPointerRender();
  };

  const hideCursor = () => {
    cursor?.classList.remove("is-visible");
    pointer.targetX = window.innerWidth / 2;
    pointer.targetY = window.innerHeight / 2;
    requestPointerRender();
  };

  window.addEventListener("pointermove", updatePointer, { passive: true });
  document.documentElement.addEventListener("mouseleave", hideCursor);

  hoverTargets.forEach((target) => {
    target.addEventListener("pointerenter", () => {
      cursor?.classList.add("is-hovering");
    });

    target.addEventListener("pointerleave", () => {
      cursor?.classList.remove("is-hovering");
    });
  });
};

export const initParallax = () => {
  if (prefersReducedMotion() || window.matchMedia("(hover: none)").matches) {
    return;
  }

  const elements = [...document.querySelectorAll("[data-parallax]")];

  if (elements.length === 0) {
    return;
  }

  window.addEventListener(
    "pointermove",
    (event) => {
      const normalizedX = event.clientX / window.innerWidth - 0.5;
      const normalizedY = event.clientY / window.innerHeight - 0.5;

      elements.forEach((element) => {
        const strength = Number(element.dataset.parallax ?? 0.2);
        const x = normalizedX * 80 * strength;
        const y = normalizedY * 80 * strength;

        element.style.setProperty("--parallax-x", x.toFixed(2));
        element.style.setProperty("--parallax-y", y.toFixed(2));
      });
    },
    { passive: true },
  );
};

export const initMagneticElements = () => {
  if (prefersReducedMotion() || window.matchMedia("(hover: none)").matches) {
    return;
  }

  const elements = document.querySelectorAll("[data-magnetic]");

  elements.forEach((element) => {
    element.addEventListener("pointermove", (event) => {
      const rect = element.getBoundingClientRect();
      const x = event.clientX - rect.left - rect.width / 2;
      const y = event.clientY - rect.top - rect.height / 2;

      element.style.setProperty("--magnetic-x", (x * 0.16).toFixed(2));
      element.style.setProperty("--magnetic-y", (y * 0.16).toFixed(2));
    });

    element.addEventListener("pointerleave", () => {
      element.style.setProperty("--magnetic-x", "0");
      element.style.setProperty("--magnetic-y", "0");
    });
  });
};

export const initClock = () => {
  const clock = document.querySelector("[data-clock]");

  if (!clock) {
    return;
  }

  const formatter = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const update = () => {
    clock.textContent = `CN / ${formatter.format(new Date())}`;
  };

  update();
  window.setInterval(update, 1000);
};

const initHeroScrollScene = (particleField) => {
  const hero = document.querySelector(".hero");
  const stage = document.querySelector("[data-hero-stage]");
  const aperture = document.querySelector("[data-hero-aperture]");
  const blades = [...document.querySelectorAll("[data-aperture-blade]")];
  const titleLines = [...document.querySelectorAll(".hero__title-line")];
  const subtitle = document.querySelector(".hero__subtitle");
  const bottom = document.querySelector(".hero__bottom");

  if (!hero || !stage || !aperture || titleLines.length === 0) {
    return;
  }

  const media = gsap.matchMedia();
  const triggerConfig = {
    trigger: hero,
    start: "top top",
    end: "bottom top",
    scrub: 0.85,
    invalidateOnRefresh: true,
    onUpdate: (self) => particleField?.setScrollProgress(self.progress),
  };

  media.add("(min-width: 62.01rem)", () => {
    const scene = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: triggerConfig,
    });

    scene
      .to(
        stage,
        {
          clipPath: "inset(10% 5% 13% 43% round 2rem)",
          yPercent: 7,
          scale: 0.96,
        },
        0,
      )
      .to(aperture, { opacity: 0.74, scale: 0.96 }, 0)
      .to(blades[0], { yPercent: 62 }, 0)
      .to(blades[1], { xPercent: -52 }, 0)
      .to(blades[2], { yPercent: -62 }, 0)
      .to(blades[3], { xPercent: 52 }, 0)
      .to(
        titleLines,
        {
          x: (index) => ["-6vw", "3vw", "-2vw"][index] ?? 0,
          yPercent: (index) => 8 + index * 7,
          opacity: (index) => 0.28 + index * 0.12,
          stagger: 0.025,
        },
        0,
      )
      .to(subtitle, { y: -36, opacity: 0 }, 0)
      .to(bottom, { y: -24, opacity: 0 }, 0);
  });

  media.add("(max-width: 62rem)", () => {
    gsap.to(stage, {
      yPercent: 3,
      scale: 0.985,
      ease: "none",
      scrollTrigger: {
        ...triggerConfig,
        scrub: 0.45,
      },
    });
  });
};

const initScrubTypography = () => {
  const lines = [...document.querySelectorAll("[data-scrub-line]")];
  const media = gsap.matchMedia();

  media.add("(min-width: 48.01rem)", () => {
    lines.forEach((line, index) => {
      gsap.fromTo(
        line,
        {
          clipPath: "inset(0 0 100% 0)",
          x: index % 2 === 0 ? -44 : 44,
          opacity: 0.2,
        },
        {
          clipPath: "inset(0 0 0% 0)",
          x: 0,
          opacity: 1,
          ease: "none",
          scrollTrigger: {
            trigger: line,
            start: "top 92%",
            end: "top 48%",
            scrub: 0.72,
          },
        },
      );
    });
  });
};

const initCinematicMask = () => {
  const stage = document.querySelector("[data-cinematic-mask]");

  if (!stage) {
    return;
  }

  const media = gsap.matchMedia();

  media.add("(min-width: 48.01rem)", () => {
    gsap.fromTo(
      stage,
      {
        clipPath: "inset(17% 10% 17% 10% round 2.4rem)",
        scale: 0.94,
      },
      {
        clipPath: "inset(0% 0% 0% 0% round 1.5rem)",
        scale: 1,
        ease: "none",
        scrollTrigger: {
          trigger: stage,
          start: "top 92%",
          end: "top 32%",
          scrub: 0.8,
        },
      },
    );
  });
};

const initArchiveMotion = () => {
  const shots = [...document.querySelectorAll("[data-archive-shot]")];

  if (shots.length === 0) {
    return;
  }

  const media = gsap.matchMedia();

  media.add("(min-width: 48.01rem)", () => {
    shots.forEach((shot, index) => {
      const imagePlane = shot.querySelector("[data-archive-media]");

      if (!imagePlane) {
        return;
      }

      const direction = index % 2 === 0 ? 1 : -1;

      gsap.fromTo(
        imagePlane,
        { yPercent: -3.5 * direction },
        {
          yPercent: 3.5 * direction,
          ease: "none",
          scrollTrigger: {
            trigger: shot,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.72,
          },
        },
      );
    });
  });
};

const initCounters = () => {
  const counters = [...document.querySelectorAll("[data-counter]")];

  counters.forEach((counter) => {
    const rawTarget = counter.dataset.counter ?? "";
    const target = Number.parseFloat(rawTarget);

    if (!Number.isFinite(target)) {
      return;
    }

    const suffix = counter.textContent.replace(rawTarget, "");
    const padding = rawTarget.length;
    const state = { value: 0 };

    counter.textContent = `${String(0).padStart(padding, "0")}${suffix}`;

    ScrollTrigger.create({
      trigger: counter,
      start: "top 86%",
      once: true,
      onEnter: () => {
        gsap.to(state, {
          value: target,
          duration: 1.9,
          ease: "power3.out",
          onUpdate: () => {
            const value = Math.round(state.value);
            counter.textContent = `${String(value).padStart(padding, "0")}${suffix}`;
          },
        });
      },
    });
  });
};

const initChapterTracking = () => {
  const items = [...document.querySelectorAll("[data-chapter-item]")];

  items.forEach((item) => {
    ScrollTrigger.create({
      trigger: item,
      start: "top 66%",
      end: "bottom 34%",
      toggleClass: { targets: item, className: "is-current" },
    });
  });
};

const initCaseScrollScene = () => {
  const section = document.querySelector("[data-cases-scene]");
  const viewport = document.querySelector("[data-case-viewport]");
  const track = document.querySelector("[data-case-track]");
  const cards = [...document.querySelectorAll("[data-case-card]")];

  if (!section || !viewport || !track || cards.length < 2) {
    return;
  }

  const media = gsap.matchMedia();

  media.add("(min-width: 75rem)", () => {
    section.classList.add("is-scroll-pinned");

    const distance = () =>
      Math.max(track.scrollWidth - viewport.clientWidth, 0);
    const clamp = (value) => Math.min(Math.max(value, 0), 1);
    const holdDuration = 0.62;
    const transitionDuration = 0.38;
    const totalDuration =
      cards.length * holdDuration + (cards.length - 1) * transitionDuration;
    const cardCenters = cards.map((_, index) => {
      return (
        (holdDuration / 2 + index * (holdDuration + transitionDuration)) /
        totalDuration
      );
    });
    const firstCenter = cardCenters[0];
    const lastCenter = cardCenters[cardCenters.length - 1];
    const positionFor = (index) => {
      const card = cards[index];
      const centered =
        viewport.clientWidth / 2 - (card.offsetLeft + card.offsetWidth / 2);

      return Math.min(Math.max(centered, -distance()), 0);
    };
    const toVisualProgress = (progress) =>
      clamp((progress - firstCenter) / (lastCenter - firstCenter));
    const timeline = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: () => `+=${distance() + window.innerHeight * cards.length * 0.55}`,
        pin: true,
        scrub: 0.82,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          section.dispatchEvent(
            new CustomEvent("caseprogress", {
              detail: { progress: toVisualProgress(self.progress) },
            }),
          );
        },
      },
    });

    timeline.to(track, {
      x: () => positionFor(0),
      duration: holdDuration,
    });

    cards.slice(1).forEach((_, index) => {
      const cardIndex = index + 1;

      timeline
        .to(track, {
          x: () => positionFor(cardIndex),
          duration: transitionDuration,
          ease: "power2.inOut",
        })
        .to(track, {
          x: () => positionFor(cardIndex),
          duration: holdDuration,
        });
    });

    section.__caseScrollScene = timeline.scrollTrigger;
    section.__caseScrollProgressMap = (progress) => {
      const scaled = clamp(progress) * (cards.length - 1);
      const leftIndex = Math.floor(scaled);
      const rightIndex = Math.min(leftIndex + 1, cards.length - 1);
      const mix = scaled - leftIndex;

      return (
        cardCenters[leftIndex] +
        (cardCenters[rightIndex] - cardCenters[leftIndex]) * mix
      );
    };

    return () => {
      delete section.__caseScrollScene;
      delete section.__caseScrollProgressMap;
      section.classList.remove("is-scroll-pinned");
      track.style.removeProperty("transform");
    };
  });
};

const initScrollChoreography = (particleField) => {
  if (prefersReducedMotion()) {
    return;
  }

  document.documentElement.classList.add("has-scroll-choreography");
  initHeroScrollScene(particleField);
  initScrubTypography();
  initCinematicMask();
  initArchiveMotion();
  initCounters();
  initChapterTracking();
  initCaseScrollScene();

  window.requestAnimationFrame(() => ScrollTrigger.refresh());
};

export const initMotion = ({ particleField } = {}) => {
  initPreloader();
  initRevealObserver();
  initScrollProgress();
  initSectionTracking();
  initPointerField();
  initParallax();
  initMagneticElements();
  initClock();
  initScrollChoreography(particleField);
};
